/**
 * Model Configuration
 * 
 * Defines all available AI models with their capabilities and configurations.
 * This is the single source of truth for model information across the application.
 */

import { AIProvider, AIModelConfig } from '../AIProvider.types';

/**
 * OpenAI Models
 */
export const OPENAI_MODELS: AIModelConfig[] = [
  {
    id: 'gpt-4o',
    name: 'gpt-4o',
    provider: AIProvider.OPENAI,
    displayName: 'GPT-4o',
    maxTokens: 128000,
    maxOutputTokens: 16384,
    supportsJsonMode: true,
    supportsStreaming: true,
    speed: 'fast',
    isRecommended: false,
    capabilities: ['vision', 'function_calling', 'json_mode']
  },
  {
    id: 'gpt-4-1106-preview',
    name: 'gpt-4-1106-preview',
    provider: AIProvider.OPENAI,
    displayName: 'GPT-4 Turbo Preview',
    maxTokens: 128000,
    supportsJsonMode: true,
    supportsStreaming: true,
    speed: 'balanced',
    isRecommended: false,
    capabilities: ['vision', 'function_calling', 'json_mode']
  },
  {
    id: 'gpt-5-nano',
    name: 'gpt-5-nano',
    provider: AIProvider.OPENAI,
    displayName: 'GPT-5 Nano',
    maxTokens: 400000,
    maxOutputTokens: 128000,
    isSmall: true,
    upgradeModelId: 'gpt-5-mini',
    supportsJsonMode: true,
    supportsStreaming: true,
    speed: 'very-fast',
    isRecommended: false,
    capabilities: ['function_calling', 'json_mode']
  },
  {
    id: 'gpt-5-mini',
    name: 'gpt-5-mini',
    provider: AIProvider.OPENAI,
    displayName: 'GPT-5 Mini',
    maxTokens: 400000,
    maxOutputTokens: 128000,
    supportsJsonMode: true,
    supportsStreaming: true,
    speed: 'fast',
    isRecommended: true,
    capabilities: ['vision', 'function_calling', 'json_mode']
  },
  {
    id: 'gpt-5.2',
    name: 'gpt-5.2',
    provider: AIProvider.OPENAI,
    displayName: 'GPT-5.2',
    maxTokens: 400000,
    maxOutputTokens: 128000,
    supportsJsonMode: true,
    supportsStreaming: true,
    speed: 'balanced',
    isRecommended: false,
    capabilities: ['vision', 'function_calling', 'json_mode', 'reasoning']
  }
];

/**
 * Anthropic Models
 */
export const ANTHROPIC_MODELS: AIModelConfig[] = [
  {
    id: 'claude-haiku-4.5',
    name: 'claude-haiku-4-5-20251001',
    provider: AIProvider.ANTHROPIC,
    displayName: 'Claude Haiku 4.5',
    maxTokens: 64000,
    isSmall: true,
    upgradeModelId: 'claude-sonnet-4.5',
    supportsJsonMode: true,
    supportsStreaming: true,
    speed: 'very-fast',
    isRecommended: true,
    capabilities: ['vision', 'tool_use', 'json_mode']
  },
  {
    id: 'claude-sonnet-4.5',
    name: 'claude-sonnet-4-5-20250929',
    provider: AIProvider.ANTHROPIC,
    displayName: 'Claude Sonnet 4.5',
    maxTokens: 64000,
    supportsJsonMode: true,
    supportsStreaming: true,
    speed: 'fast',
    isRecommended: false,
    capabilities: ['vision', 'tool_use', 'json_mode']
  },
  {
    id: 'claude-opus-4.5',
    name: 'claude-opus-4-5-20251101',
    provider: AIProvider.ANTHROPIC,
    displayName: 'Claude Opus 4.5',
    maxTokens: 64000,
    supportsJsonMode: true,
    supportsStreaming: true,
    speed: 'balanced',
    isRecommended: false,
    capabilities: ['vision', 'tool_use', 'json_mode', 'extended_thinking', 'reasoning']
  }
];

/**
 * Google Models
 */
export const GOOGLE_MODELS: AIModelConfig[] = [
  {
    id: 'gemini-2.5-flash',
    name: 'gemini-2.5-flash',
    provider: AIProvider.GOOGLE,
    displayName: 'Gemini 2.5 Flash',
    maxTokens: 65536,
    supportsJsonMode: true,
    supportsStreaming: true,
    speed: 'very-fast',
    isRecommended: false,
    capabilities: ['vision', 'function_calling', 'json_mode']
  },
  {
    id: 'gemini-2.5-pro',
    name: 'gemini-2.5-pro',
    provider: AIProvider.GOOGLE,
    displayName: 'Gemini 2.5 Pro',
    maxTokens: 65536,
    supportsJsonMode: true,
    supportsStreaming: true,
    speed: 'balanced',
    isRecommended: false,
    capabilities: ['vision', 'function_calling', 'json_mode']
  },
  {
    id: 'gemini-3-flash-preview',
    name: 'gemini-3-flash-preview',
    provider: AIProvider.GOOGLE,
    displayName: 'Gemini 3 Flash',
    maxTokens: 64000,
    supportsJsonMode: true,
    supportsStreaming: true,
    speed: 'fast',
    isRecommended: true,
    capabilities: ['vision', 'function_calling', 'json_mode']
  },
  {
    id: 'gemini-3-pro-preview',
    name: 'gemini-3-pro-preview',
    provider: AIProvider.GOOGLE,
    displayName: 'Gemini 3 Pro',
    maxTokens: 64000,
    supportsJsonMode: true,
    supportsStreaming: true,
    speed: 'balanced',
    isRecommended: false,
    capabilities: ['vision', 'function_calling', 'json_mode', 'reasoning']
  }
];

/**
 * xAI Models
 */
export const XAI_MODELS: AIModelConfig[] = [
  {
    id: 'grok-3-mini',
    name: 'grok-3-mini',
    provider: AIProvider.XAI,
    displayName: 'Grok 3 Mini',
    maxTokens: 128000,
    supportsJsonMode: true,
    supportsStreaming: true,
    speed: 'very-fast',
    isRecommended: false,
    capabilities: ['function_calling', 'json_mode']
  },
  {
    id: 'grok-4-0709',
    name: 'grok-4-0709',
    provider: AIProvider.XAI,
    displayName: 'Grok 4',
    maxTokens: 256000,
    supportsJsonMode: true,
    supportsStreaming: true,
    speed: 'balanced',
    isRecommended: false,
    capabilities: ['vision', 'function_calling', 'json_mode', 'reasoning']
  },
  {
    id: 'grok-4-1-fast',
    name: 'grok-4-1-fast-non-reasoning',
    provider: AIProvider.XAI,
    displayName: 'Grok 4.1 Fast',
    maxTokens: 256000,
    supportsJsonMode: true,
    supportsStreaming: true,
    speed: 'fast',
    isRecommended: true,
    capabilities: ['vision', 'function_calling', 'json_mode']
  }
];

/**
 * Combined list of all models
 */
export const ALL_MODELS: AIModelConfig[] = [
  ...OPENAI_MODELS,
  ...ANTHROPIC_MODELS,
  ...GOOGLE_MODELS,
  ...XAI_MODELS
];

/**
 * Model configurations indexed by model ID for quick lookup
 */
export const MODEL_CONFIGS: Record<string, AIModelConfig> = {
  ...Object.fromEntries(OPENAI_MODELS.map(m => [m.id, m])),
  ...Object.fromEntries(ANTHROPIC_MODELS.map(m => [m.id, m])),
  ...Object.fromEntries(GOOGLE_MODELS.map(m => [m.id, m])),
  ...Object.fromEntries(XAI_MODELS.map(m => [m.id, m]))
};

/**
 * Get model configuration by ID
 */
export const getModelConfig = (modelId: string): AIModelConfig | undefined => {
  return MODEL_CONFIGS[modelId];
};

/**
 * Get all models for a specific provider
 */
export const getModelsByProvider = (provider: AIProvider): AIModelConfig[] => {
  return ALL_MODELS.filter(m => m.provider === provider);
};

/**
 * Get the recommended model for a provider
 */
export const getRecommendedModel = (provider: AIProvider): AIModelConfig | undefined => {
  return ALL_MODELS.find(m => m.provider === provider && m.isRecommended);
};

/**
 * Default model ID to use when none is specified
 */
export const DEFAULT_MODEL_ID = 'claude-haiku-4.5';

/**
 * Default provider to use when none is specified
 */
export const DEFAULT_PROVIDER = AIProvider.OPENAI;
